/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import * as yup from 'yup';
import * as empApi from '../../lib/api/employee';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import EmployeeForm from '../../components/employeemng/EmployeeForm';
import { setEmployee } from '../../modules/empModule/employee';
import AskDialog from '../../components/common/dialog/AskDialog';
import useActionPermission from '../../hooks/useActionPermission';

const isAdminRole = (role) =>
  typeof role === 'string' && role.toUpperCase().includes('ADMIN');

const EmployeeFormContainer = ({ type }) => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // 관리자 ↔ 일반 전환 스위치 노출 판단 - 실제 차단은 서버(PUT /user/info/{usrUuid}/role)가
  // 한다. bito 권한 체계 검토(2026-10-23) 오너 추가 요구사항.
  const { allowed: isAdmin } = useActionPermission('HR_RESUME_VIS_ADMIN');
  const [roleConfirmOpen, setRoleConfirmOpen] = useState(false);

  const { employee } = useSelector(({ empModule }) => ({
    employee: empModule.employee.employee,
  }));

  const initialValues = {
    usrNo: employee && employee.usrNo !== null ? employee.usrNo : '',
    usrNm: employee && employee.usrNm !== null ? employee.usrNm : '',
    password: employee && employee.password !== null ? employee.password : '',
    confirmPassword: '',
    emlAddr: employee && employee.emlAddr !== null ? employee.emlAddr : '',
    dptUuid: employee && employee.dptUuid !== null ? employee.dptUuid : '',
    gradeUuid:
      employee && employee.gradeUuid !== null ? employee.gradeUuid : '',
    policyUuid: 
      employee && employee.policyUuid !== null ? employee.policyUuid : '',
    telNo: employee && employee.telNo !== null ? employee.telNo : '',
    postAddrTxt:
      employee && employee.postAddrTxt !== null ? employee.postAddrTxt : '',
    postAddrNm:
      employee && employee.postAddrNm !== null ? employee.postAddrNm : '',
    entryDt: employee && employee.entryDt !== null ? employee.entryDt : '',
//    gender: employee && employee.gender !== null ? employee.gender : '',
    privilegeList:
      employee && employee.privilegeList !== null ? employee.privilegeList : [],
    useYn:
      employee && employee.useYn !== null && employee.useYn === 'Y'
        ? true
        : false,
    infoDesc: employee && employee.infoDesc !== null ? employee.infoDesc : '',
  };

  const validationSchema = yup.object({
    usrNo: yup.string().required('사번을 입력하세요'),
    usrNm: yup.string().required('이름을 입력하세요'),
    dptUuid: yup.string().required('부서를 선택하세요'),
    gradeUuid: yup.string().required('직급을 선택하세요'),
    emlAddr: yup
      .string('')
      .email('이메일 형식이 아닙니다.')
      .required('이메일을 입력하세요'),
    entryDt: yup.date().nullable(''),
    telNo: yup.string().required('연락처를 입력하세요'),
    postAddrNm: yup.string().nullable('우편번호를 입력하세요'),
    postAddrTxt: yup.string().nullable('주소를 입력하세요'),
    password: yup
      .string()
      .concat(
        type === 'add' ? yup.string().required('비밀번호를 입력하세요') : null,
      )
      .min(6, '비밀번호는 최소 6글자 이상입니다'),
    confirmPassword: yup
      .string()
      .when('password', (password, schema) => {
        if (password || type === 'add')
          return schema.required('비밀번호 확인을 입력하세요');
      })
      .oneOf([yup.ref('password')], '비밀번호와 일치해야 합니다'),
  });

  useEffect(() => {
    if (type === 'edit') {
      delete initialValues.password;
      delete initialValues.confirmPassword;
    }
  }, [type, validationSchema]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
    },
  });

  const onSave = async (values) => {
    const dataToSubmit = {
      usrNo: values.usrNo.trim(),
      usrNm: values.usrNm.trim(),
      dptUuid: values.dptUuid,
      gradeUuid: values.gradeUuid,
      policyUuid: values.policyUuid,
      emlAddr: values.emlAddr.trim(),
//      gender: values.gender,
      telNo: values.telNo.trim(),
      entryDt: values.entryDt
        ? dayjs(values.entryDt).format('YYYY-MM-DD')
        : dayjs(new Date()).format('YYYY-MM-DD'),
      postAddrNm: values.postAddrNm.trim(),
      postAddrTxt: values.postAddrTxt.trim(),
      privilegeList: values?.privilegeList,
      useYn: values.useYn ? 'Y' : 'N',
      infoDesc: values.infoDesc,
    };

    if (type === 'add') {
      dataToSubmit.passwd = values.password;

      try {
        const res = await empApi.addEmployee(dataToSubmit);
        if (res.data) {
          navigate(`/employeemng/${res.data.info.usrUuid}`);
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    } else {
      dataToSubmit.usrUuid = employee.usrUuid;
      //console.log(JSON.stringify(dataToSubmit), 'onSave datatosubmit');
      try {
        const res = await empApi.editEmployee(dataToSubmit);
        console.log(res.data.info);
        if (res.data) {
          dispatch(setEmployee(res.data.info));
          enqueueSnackbar('사용자 정보 수정 성공', {
            variant: 'success',
          });
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    }
  };

  const onRoleToggleConfirm = async () => {
    setRoleConfirmOpen(false);
    const nextRole = isAdminRole(employee?.role) ? 'USER_ROLE' : 'ADMIN_ROLE';
    try {
      const res = await empApi.changeUsrRole(employee.usrUuid, nextRole);
      if (res.data?.info) {
        dispatch(setEmployee(res.data.info));
      }
      enqueueSnackbar(
        nextRole === 'ADMIN_ROLE'
          ? '관리자로 변경했습니다.'
          : '일반 사용자로 변경했습니다.',
        { variant: 'success' },
      );
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <>
      {/* 관리자만 보이고, 수정 화면(기존 사원)에서만 노출 - 신규 등록은 항상 일반으로
          만들어지고(서버 기본값) 이 화면에서는 역할을 같이 주지 않는다. bito 권한 체계
          검토(2026-10-23) 오너 추가 요구사항. */}
      {type === 'edit' && isAdmin && employee && employee.usrUuid && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">관리자 권한</Typography>
            <FormControlLabel
              labelPlacement="start"
              label={isAdminRole(employee.role) ? '관리자' : '일반'}
              control={
                <Switch
                  checked={isAdminRole(employee.role)}
                  onChange={() => setRoleConfirmOpen(true)}
                />
              }
            />
          </Box>
        </Paper>
      )}
      <EmployeeForm formik={formik} employee={employee} />
      <AskDialog
        visible={roleConfirmOpen}
        title="권한 변경"
        description={
          isAdminRole(employee?.role)
            ? `${employee?.usrNm ?? ''}님을 일반 사용자로 변경하시겠습니까?`
            : `${employee?.usrNm ?? ''}님을 관리자로 지정하시겠습니까?`
        }
        confirmText="변경"
        onConfirm={onRoleToggleConfirm}
        onCancle={() => setRoleConfirmOpen(false)}
      />
    </>
  );
};

export default EmployeeFormContainer;
